import React from 'react';
import {
  Image,
  ImageBackground,
  View,
  type ImageSourcePropType,
  type ImageResizeMode,
  type ViewStyle,
  type StyleProp,
} from 'react-native';

import { Button, Text } from '~/components';
import { isWeb } from '~/utils/device';

interface BannerProps {
  title: string;
  description: string;
  buttonLabel?: string;
  secondaryButtonLabel?: string;
  thirdButtonLabel?: string;
  image?: ImageSourcePropType;
  onPressButton?: () => void;
  onPressSecondButton?: () => void;
  onPressThirdButton?: () => void;
  textBlack?: boolean;
  buttonWhite?: boolean;
  rightImage?: ImageSourcePropType;
  topImage?: ImageSourcePropType;
  fullWidth?: boolean;
  rightImageResizeMode?: ImageResizeMode;
  style?: StyleProp<ViewStyle>;
}

const Wrapper = ({
  children,
  image,
  fullWidth,
  style,
  topImage,
}: {
  children: React.ReactNode;
  image?: ImageSourcePropType;
  fullWidth?: boolean;
  topImage?: ImageSourcePropType;
  style?: StyleProp<ViewStyle>;
}) => {
  if (image) {
    return (
      <ImageBackground
        source={image}
        className={`justify-between bg-light-100 rounded-lg overflow-hidden ${
          !fullWidth && 'sm:w-[335px] h-[350px]'
        } border border-gray-100 ${
          topImage ? 'flex-col' : 'flex-row items-center'
        } `}
      >
        {children}
      </ImageBackground>
    );
  }
  return (
    <View
      style={style}
      className={`justify-between bg-light-100 rounded-lg overflow-hidden ${
        !fullWidth && 'sm:max-w-[335px]'
      } border border-gray-100 ${
        topImage ? 'flex-col' : 'flex-row items-center'
      } `}
    >
      {children}
    </View>
  );
};

const Banner: React.FC<BannerProps> = ({
  title,
  description,
  buttonLabel,
  secondaryButtonLabel,
  thirdButtonLabel,
  onPressButton,
  onPressSecondButton,
  onPressThirdButton,
  image,
  textBlack,
  buttonWhite,
  rightImage,
  topImage,
  fullWidth,
  rightImageResizeMode = 'cover',
  style,
}) => {
  return (
    <Wrapper
      fullWidth={fullWidth}
      image={image}
      style={style}
      topImage={topImage}
    >
      {!!topImage && (
        <Image className="w-[122px] h-[98px]  mr-auto" source={topImage} />
      )}
      <View
        className={`${
          fullWidth && rightImage ? 'flex-1' : 'w-full sm:w-[60%]'
        } p-4`}
      >
        <Text
          className={`${
            textBlack ? 'text-gray-700' : 'text-light-100'
          } text-lg font-medium`}
        >
          {title}
        </Text>

        <Text
          className={`${
            textBlack ? 'text-gray-400' : 'text-light-100'
          }  mt-2 text-sm min-h-[60px]`}
          numberOfLines={fullWidth ? 3 : undefined}
        >
          {description}
        </Text>

        {!!buttonLabel && (
          <View className="flex-row items-center mt-4">
            <Button
              variant={buttonWhite ? 'tertiary' : 'primary'}
              className="px-4 py-2 rounded-2xl sm:py-1"
              onPress={onPressButton}
            >
              {buttonLabel}
            </Button>

            {!!secondaryButtonLabel && (
              <Button
                variant={'secondary'}
                className="px-4 py-2 rounded-2xl sm:py-1 ml-2 bg-transparent border-light-100"
                onPress={onPressSecondButton}
              >
                <Text className="text-light-100">{secondaryButtonLabel}</Text>
              </Button>
            )}

            {!!thirdButtonLabel && (
              <Button
                variant={'tertiary'}
                className="px-4 py-2 rounded-2xl sm:py-1 ml-2"
                onPress={onPressThirdButton}
              >
                {thirdButtonLabel}
              </Button>
            )}
          </View>
        )}
      </View>
      {!!rightImage && (
        <Image
          className={`h-[180px] flex-1 ml-auto ${isWeb && 'max-w-[150px]'}`}
          resizeMode={rightImageResizeMode}
          source={rightImage}
        />
      )}
    </Wrapper>
  );
};

export default Banner;
